import is from '@sindresorhus/is';
import type { FunctionComponent, KeyboardEvent, PropsWithChildren, RefObject } from 'react';
import { createContext, useCallback, useContext, useMemo, useRef } from 'react';
import type { StoreApi } from 'zustand';
import { createStore, useStore } from 'zustand';

import { useArrowKeyNet } from '~/ui/core/focus/use-arrow-key-net';

export type BoardColumnId = 'cluster' | 'family' | 'role';

const JOB_ARCHITECTURE_COLUMN_ORDER: readonly BoardColumnId[] = ['family', 'cluster', 'role'];

const MOVEMENT_KEYS: ReadonlySet<string> = new Set(['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Home', 'End']);

type BoardFocusItem = {
    readonly id: string;
    readonly columnId: string;
    readonly hasExpandableContent?: boolean;
    isActiveItem: boolean;
    readonly isExpanded?: boolean;
    isSelectedItem: boolean;
    readonly onDeselect?: () => void;
    readonly onNavigateSelect: () => void;
    readonly onOpen: () => void;
    readonly onSelectAll?: (ids: readonly string[]) => void;
    readonly onToggleExpanded?: () => void;
    readonly parentId?: string;
    readonly ref: RefObject<HTMLDivElement | null>;
};

type BoardFocusState = {
    readonly focusedItemId: string | null;
    readonly tabStopItemId: string | null;
};

type FocusIntent = {
    managed: boolean;
    pointer: boolean;
};

type BoardFocusContextType = {
    readonly handleItemFocus: (id: string) => void;
    readonly registerItem: (item: BoardFocusItem) => () => void;
    readonly setNavigationSuspended: (isSuspended: boolean) => void;
    readonly store: StoreApi<BoardFocusState>;
    readonly updateItemState: (id: string, state: Pick<BoardFocusItem, 'isActiveItem' | 'isSelectedItem'>) => void;
};

const BoardFocusContext = createContext<BoardFocusContextType | null>(null);

const isBefore = (a: HTMLElement, b: HTMLElement): boolean =>
    (a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING) !== 0;

const sortByDomOrder = (items: readonly BoardFocusItem[]): readonly BoardFocusItem[] =>
    [...items].sort((a, b) => {
        const elementA = a.ref.current;
        const elementB = b.ref.current;

        if (is.null(elementA) || is.null(elementB)) {
            return 0;
        }

        return isBefore(elementA, elementB) ? -1 : 1;
    });

const findNavigationAnchor = (items: readonly BoardFocusItem[]): BoardFocusItem | undefined =>
    items.find((item) => item.isActiveItem) ?? items.find((item) => item.isSelectedItem);

const isSelectAllChord = (event: KeyboardEvent<HTMLDivElement>): boolean =>
    (event.metaKey || event.ctrlKey) && !event.altKey && !event.shiftKey && event.key.toLowerCase() === 'a';

export const BoardFocusProvider: FunctionComponent<PropsWithChildren<{ readonly columnOrder?: readonly string[] }>> = ({
    children,
    columnOrder = JOB_ARCHITECTURE_COLUMN_ORDER,
}) => {
    const itemsRef = useRef<Map<string, BoardFocusItem>>(new Map());
    const storeRef = useRef<StoreApi<BoardFocusState>>(null);
    storeRef.current ??= createStore<BoardFocusState>(() => ({ focusedItemId: null, tabStopItemId: null }));
    const store = storeRef.current;

    const getMountedItems = useCallback(
        (): readonly BoardFocusItem[] => [...itemsRef.current.values()].filter((item) => item.ref.current !== null),
        [],
    );

    const getColumnItems = useCallback(
        (columnId: string): readonly BoardFocusItem[] =>
            sortByDomOrder(getMountedItems().filter((item) => item.columnId === columnId)),
        [getMountedItems],
    );

    const scopeRef = useRef<HTMLDivElement>(null);
    const isNavigationSuspendedRef = useRef(false);
    const setNavigationSuspended = useCallback((isSuspended: boolean): void => {
        isNavigationSuspendedRef.current = isSuspended;
    }, []);

    // The focused item only anchors the tab stop while focus is inside the board.
    // Once focus has moved on (search dialog, tile), a selection made there must win,
    // or returning focus would land on the stale card and re-select it.
    const isFocusInsideBoard = useCallback(
        (): boolean => scopeRef.current?.contains(document.activeElement) === true,
        [],
    );

    const recomputeTabStop = useCallback((): void => {
        const { focusedItemId, tabStopItemId } = store.getState();
        const nextTabStopItemId = ((): string | null => {
            if (focusedItemId !== null && itemsRef.current.has(focusedItemId) && isFocusInsideBoard()) {
                return focusedItemId;
            }

            const mountedItems = getMountedItems();
            const navigationAnchor = findNavigationAnchor(mountedItems);

            if (!is.undefined(navigationAnchor)) {
                return navigationAnchor.id;
            }

            if (tabStopItemId !== null && itemsRef.current.has(tabStopItemId)) {
                return tabStopItemId;
            }

            return sortByDomOrder(mountedItems)[0]?.id ?? null;
        })();

        if (nextTabStopItemId !== tabStopItemId) {
            store.setState({ tabStopItemId: nextTabStopItemId });
        }
    }, [store, getMountedItems, isFocusInsideBoard]);

    const focusIntentRef = useRef<FocusIntent>({ managed: false, pointer: false });

    const focusElementManaged = useCallback((element: HTMLElement | null, options?: FocusOptions): void => {
        focusIntentRef.current.managed = true;

        try {
            element?.focus(options);
        } finally {
            focusIntentRef.current.managed = false;
        }
    }, []);

    // Recovery only restores the roving tab stop after the focused card unmounts; it must never
    // select, or a stale card (e.g. the previous architecture's family) is written back into the URL.
    const recoverFocusToTabStop = useCallback((): void => {
        const { tabStopItemId } = store.getState();
        const next = tabStopItemId === null ? undefined : itemsRef.current.get(tabStopItemId);
        const nextElement = next?.ref.current ?? null;

        if (nextElement === null) {
            return;
        }

        queueMicrotask(() => {
            if (nextElement.isConnected && document.activeElement === document.body) {
                focusElementManaged(nextElement, { preventScroll: true });
            }
        });
    }, [focusElementManaged, store]);

    const registerItem = useCallback(
        (item: BoardFocusItem): (() => void) => {
            itemsRef.current.set(item.id, item);
            recomputeTabStop();

            return () => {
                if (itemsRef.current.get(item.id) !== item) {
                    return;
                }

                const wasFocused = store.getState().focusedItemId === item.id;
                itemsRef.current.delete(item.id);

                if (wasFocused) {
                    store.setState({ focusedItemId: null });
                }

                recomputeTabStop();

                if (wasFocused) {
                    recoverFocusToTabStop();
                }
            };
        },
        [recomputeTabStop, recoverFocusToTabStop, store],
    );

    const updateItemState = useCallback(
        (id: string, state: Pick<BoardFocusItem, 'isActiveItem' | 'isSelectedItem'>): void => {
            const item = itemsRef.current.get(id);

            if (is.undefined(item)) {
                return;
            }

            item.isActiveItem = state.isActiveItem;
            item.isSelectedItem = state.isSelectedItem;
            recomputeTabStop();
        },
        [recomputeTabStop],
    );

    const handleItemFocus = useCallback(
        (id: string): void => {
            store.setState({ focusedItemId: id, tabStopItemId: id });

            const item = itemsRef.current.get(id);

            if (
                !is.undefined(item) &&
                !focusIntentRef.current.managed &&
                !focusIntentRef.current.pointer &&
                !item.isSelectedItem
            ) {
                item.onNavigateSelect();
            }
        },
        [store],
    );

    const focusAndSelectItem = useCallback(
        (item: BoardFocusItem | undefined): void => {
            if (is.undefined(item)) {
                return;
            }

            focusElementManaged(item.ref.current);

            if (!item.isSelectedItem) {
                item.onNavigateSelect();
            }
        },
        [focusElementManaged],
    );

    const focusColumn = useCallback(
        (columnId: string | undefined): void => {
            if (is.undefined(columnId)) {
                return;
            }

            const columnItems = getColumnItems(columnId);
            const target = columnItems.find((item) => item.isSelectedItem) ?? columnItems[0];
            focusAndSelectItem(target);
        },
        [getColumnItems, focusAndSelectItem],
    );

    const focusFirstInColumn = useCallback(
        (columnId: string | undefined): void => {
            if (is.undefined(columnId)) {
                return;
            }

            focusAndSelectItem(getColumnItems(columnId)[0]);
        },
        [getColumnItems, focusAndSelectItem],
    );

    const moveWithinColumn = useCallback(
        (item: BoardFocusItem, delta: -1 | 1): void => {
            const columnItems = getColumnItems(item.columnId);
            const currentIndex = columnItems.findIndex((candidate) => candidate.id === item.id);

            if (currentIndex === -1) {
                return;
            }

            const nextIndex = (currentIndex + delta + columnItems.length) % columnItems.length;
            focusAndSelectItem(columnItems[nextIndex]);
        },
        [getColumnItems, focusAndSelectItem],
    );

    const handleArrowRight = useCallback(
        (item: BoardFocusItem): void => {
            if (item.hasExpandableContent === true) {
                if (item.isExpanded !== true) {
                    item.onToggleExpanded?.();

                    return;
                }

                const firstChild = sortByDomOrder(
                    getMountedItems().filter((candidate) => candidate.parentId === item.id),
                );
                focusAndSelectItem(firstChild[0]);

                return;
            }

            const columnIndex = columnOrder.indexOf(item.columnId);
            focusFirstInColumn(columnOrder[columnIndex + 1]);
        },
        [columnOrder, getMountedItems, focusAndSelectItem, focusFirstInColumn],
    );

    const handleArrowLeft = useCallback(
        (item: BoardFocusItem): void => {
            if (!is.undefined(item.parentId)) {
                focusAndSelectItem(itemsRef.current.get(item.parentId));

                return;
            }

            if (item.hasExpandableContent === true && item.isExpanded === true) {
                item.onToggleExpanded?.();

                return;
            }

            const columnIndex = columnOrder.indexOf(item.columnId);

            if (columnIndex <= 0) {
                return;
            }

            item.onDeselect?.();
            focusColumn(columnOrder[columnIndex - 1]);
        },
        [columnOrder, focusAndSelectItem, focusColumn],
    );

    const applyNavigationKey = useCallback(
        (item: BoardFocusItem, key: string): boolean => {
            if (isNavigationSuspendedRef.current) {
                return false;
            }

            switch (key) {
                case 'ArrowDown':
                    moveWithinColumn(item, 1);

                    return true;
                case 'ArrowUp':
                    moveWithinColumn(item, -1);

                    return true;
                case 'ArrowRight':
                    handleArrowRight(item);

                    return true;
                case 'ArrowLeft':
                    handleArrowLeft(item);

                    return true;
                case 'Home':
                    focusAndSelectItem(getColumnItems(item.columnId)[0]);

                    return true;
                case 'End':
                    focusAndSelectItem(getColumnItems(item.columnId).at(-1));

                    return true;
                case 'Enter':
                    item.onOpen();

                    return true;
                default:
                    return false;
            }
        },
        [getColumnItems, moveWithinColumn, handleArrowRight, handleArrowLeft, focusAndSelectItem],
    );

    const applySelectAll = useCallback(
        (item: BoardFocusItem): boolean => {
            const { onSelectAll } = item;

            if (is.undefined(onSelectAll)) {
                return false;
            }

            const peerIds = getMountedItems()
                .filter((candidate) => candidate.columnId === item.columnId && candidate.parentId === item.parentId)
                .map((candidate) => candidate.id);
            onSelectAll(peerIds);

            return true;
        },
        [getMountedItems],
    );

    const handleKeyDown = useCallback(
        (event: KeyboardEvent<HTMLDivElement>): void => {
            const target = event.target;

            if (!(target instanceof HTMLElement)) {
                return;
            }

            const item = getMountedItems().find((candidate) => candidate.ref.current === target);

            if (is.undefined(item)) {
                return;
            }

            if (isSelectAllChord(event)) {
                if (applySelectAll(item)) {
                    event.preventDefault();
                    event.stopPropagation();
                }

                return;
            }

            if (event.altKey || event.ctrlKey || event.metaKey) {
                return;
            }

            if (applyNavigationKey(item, event.key)) {
                event.preventDefault();
                event.stopPropagation();
            }
        },
        [getMountedItems, applyNavigationKey, applySelectAll],
    );

    const handleKeyDownCapture = useCallback(
        (event: KeyboardEvent<HTMLDivElement>): void => {
            const target = event.target;

            if (!(target instanceof HTMLElement)) {
                return;
            }

            const selectAllChord = isSelectAllChord(event);

            if (!selectAllChord && (event.altKey || event.ctrlKey || event.metaKey || !MOVEMENT_KEYS.has(event.key))) {
                return;
            }

            const item = getMountedItems().find((candidate) => {
                const card = candidate.ref.current;

                return card !== null && card !== target && card.contains(target);
            });

            if (is.undefined(item)) {
                return;
            }

            if (selectAllChord) {
                if (applySelectAll(item)) {
                    event.preventDefault();
                    event.stopPropagation();
                }

                return;
            }

            if (applyNavigationKey(item, event.key)) {
                event.preventDefault();
                event.stopPropagation();
            }
        },
        [getMountedItems, applyNavigationKey, applySelectAll],
    );

    const claimMovementKey = useCallback(
        (event: globalThis.KeyboardEvent): void => {
            const items = getMountedItems();

            if (items.length === 0 || isNavigationSuspendedRef.current) {
                return;
            }

            event.preventDefault();
            const navigationAnchor = findNavigationAnchor(items);

            if (is.undefined(navigationAnchor)) {
                focusAndSelectItem(sortByDomOrder(items)[0]);
            } else {
                applyNavigationKey(navigationAnchor, event.key);
            }
        },
        [getMountedItems, focusAndSelectItem, applyNavigationKey],
    );
    useArrowKeyNet(MOVEMENT_KEYS, claimMovementKey, scopeRef);

    const handleMouseDownCapture = useCallback((): void => {
        focusIntentRef.current.pointer = true;
        setTimeout(() => {
            focusIntentRef.current.pointer = false;
        }, 0);
    }, []);

    const contextValue = useMemo(
        () => ({ handleItemFocus, registerItem, setNavigationSuspended, store, updateItemState }),
        [handleItemFocus, registerItem, setNavigationSuspended, store, updateItemState],
    );

    return (
        <BoardFocusContext.Provider value={contextValue}>
            {/* biome-ignore lint/a11y/noStaticElementInteractions: keyboard handling for the roving tab stop of the board composite widget */}
            <div
                ref={scopeRef}
                className="contents"
                data-focus-region="board"
                data-focus-region-kind="main"
                data-testid="board-focus-scope"
                onKeyDown={handleKeyDown}
                onKeyDownCapture={handleKeyDownCapture}
                onMouseDownCapture={handleMouseDownCapture}
            >
                {children}
            </div>
        </BoardFocusContext.Provider>
    );
};

const useBoardFocus = (): BoardFocusContextType => {
    const context = useContext(BoardFocusContext);

    if (context === null) {
        throw new Error('useBoardFocus must be used within a BoardFocusProvider');
    }

    return context;
};

export const useBoardFocusItemController = (): Pick<
    BoardFocusContextType,
    'handleItemFocus' | 'registerItem' | 'updateItemState'
> => {
    const { handleItemFocus, registerItem, updateItemState } = useBoardFocus();

    return { handleItemFocus, registerItem, updateItemState };
};

export const useIsBoardTabStop = (id: string): boolean => {
    const { store } = useBoardFocus();

    return useStore(store, (state) => state.tabStopItemId === id);
};

export const useSetBoardNavigationSuspended = (): BoardFocusContextType['setNavigationSuspended'] =>
    useBoardFocus().setNavigationSuspended;
